<template>
  <div>
    <component
      :is="currentStep"
      :is-new-job="isNewJob"
      :step-count="stepsCount"
      :current-step-count="currentStepIdx + 1"
      @next-step="onNextStep"
      @previous-step="onPrevStep"
    />
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, ref, computed, watch } from 'vue'

import JobWizardStepFeatures from '@/components/CreateJob/features/JobWizardStepFeatures.vue'
import JobWizardStepDescription from '@/components/CreateJob/JobWizardStepDescription.vue'
import JobWizardStepDetails from '@/components/CreateJob/JobWizardStepDetails.vue'

import { useJobsSdk } from '~/gql'
import { useNewJobStore } from '~/store/newJob'

interface JobWizardEmits {
  (e: 'finish'): void
}

interface JobWizardProps {
  isNewJob?: boolean
}

const emit = defineEmits<JobWizardEmits>()

const props = defineProps<JobWizardProps>()

const store = useNewJobStore()
const { getPrice } = useJobsSdk()
const currentStepIdx = ref(0)

const wizardSteps = computed(() => [...(props.isNewJob ? [JobWizardStepFeatures] : []), JobWizardStepDetails, JobWizardStepDescription])

const stepsCount = computed(() => wizardSteps.value.length)

const prevStep = computed(() => wizardSteps.value[currentStepIdx.value - 1])

const currentStep = computed(() => wizardSteps.value[currentStepIdx.value])

const nextStep = computed(() => wizardSteps.value[currentStepIdx.value + 1])

const jobOffer = computed(() => store.jobOffer)

const features = computed(() => jobOffer.value.features || [])

const offerType = computed(() => jobOffer.value.offerType)

const jobOfferDirty = computed(() => store.jobOfferDirty)

watch([features, offerType], () => {
  if (!jobOfferDirty.value) return

  fetchPrice()
})

const onPrevStep = () => {
  if (!prevStep.value) return

  currentStepIdx.value -= 1
}

const onNextStep = () => {
  if (!nextStep.value) {
    return emit('finish')
  }

  window.scrollTo({ top: 0 })

  currentStepIdx.value += 1
}

const fetchPrice = async () => {
  store.updateJobOfferPrice(null)

  const res = await getPrice(
    {
      offerType: offerType.value,
      features: features.value,
    },
    {
      fetchPolicy: 'network-only',
    },
  )

  const price = res.data?.calculate

  store.updateJobOfferPrice(price ? { ...price, price: Number(price.price) } : null)
}

onBeforeMount(() => {
  fetchPrice()
})
</script>
